import { useState, useCallback, useEffect, useRef } from 'react';
import { useToolOptions } from './useToolOptions';

export interface UseSnapToGridReturn {
  /** Current snap value with Alt-override applied */
  snapActive: boolean;
  /** Base value (without override) */
  snapEnabled: boolean;
  /** Toggle base value */
  toggle: () => void;
  /** Set base value explicitly */
  setSnap: (value: boolean) => void;
}

/**
 * Snap-to-grid hook with Alt-override.
 *
 * Reads `snapToGrid` from ToolContext.options via useToolOptions.
 * Alt key temporarily inverts the snap value.
 */
export function useSnapToGrid(): UseSnapToGridReturn {
  const { options, setOption } = useToolOptions();
  const snapEnabled = options.snapToGrid;

  const [altHeld, setAltHeld] = useState(false);
  const altHeldRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Alt' && !altHeldRef.current) {
        altHeldRef.current = true;
        setAltHeld(true);
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        altHeldRef.current = false;
        setAltHeld(false);
      }
    };
    // Also reset on blur (Alt-Tab etc.)
    const handleBlur = () => {
      if (altHeldRef.current) {
        altHeldRef.current = false;
        setAltHeld(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const snapActive = altHeld ? !snapEnabled : snapEnabled;

  const toggle = useCallback(() => {
    setOption('snapToGrid', !snapEnabled);
  }, [snapEnabled, setOption]);

  const setSnap = useCallback(
    (value: boolean) => {
      setOption('snapToGrid', value);
    },
    [setOption],
  );

  return { snapActive, snapEnabled, toggle, setSnap };
}
